import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import * as locales from "@mui/material/locale";
import { DEFAULT_LANGUAGE } from "../common/MetaSetting";

type SupportedLocales = keyof typeof locales;
type ThemeMode = "light" | "dark";

// App状态接口
export interface AppState {
  currentLanguage: SupportedLocales;
  themeMode: ThemeMode;
  updateCurrentLanguage: (language: SupportedLocales) => void;
  setThemeMode: (themeMode: ThemeMode) => void;
  toggleTheme: () => void;
}

// File状态接口
export interface FileState {
  status: "idle" | "loading" | "failed";
  currentFile: any | undefined;
  files: any[];
  currentLanguage: SupportedLocales;
  setStatus: (status: "idle" | "loading" | "failed") => void;
  setCurrentFile: (file: any) => void;
  setFiles: (files: any[]) => void;
  updateCurrentFile: (updates: Partial<any>) => void;
  updateCurrentLanguage: (language: SupportedLocales) => void;
}

// 注册状态接口
export interface RegisterState {
  currentLanguage: SupportedLocales;
  updateCurrentLanguage: (language: SupportedLocales) => void;
}

// 创建App状态store
export const useAppStore = create<AppState>()(
  persist(
    devtools(
      (set, get) => ({
        currentLanguage: DEFAULT_LANGUAGE,
        themeMode: "light" as ThemeMode,
        updateCurrentLanguage: (language) => set({ currentLanguage: language }),
        setThemeMode: (themeMode) => set({ themeMode }),
        toggleTheme: () => {
          const currentTheme = get().themeMode;
          const newTheme = currentTheme === "light" ? "dark" : "light";
          set({ themeMode: newTheme });
        },
      }),
      { name: "App Store" }
    ),
    {
      name: "app-storage",
      partialize: (state) => ({
        currentLanguage: state.currentLanguage,
        themeMode: state.themeMode,
      }),
    }
  )
);

// 创建File状态store
export const useFileStore = create<FileState>()(
  devtools(
    (set) => ({
      status: "idle",
      currentFile: undefined,
      files: [],
      currentLanguage: DEFAULT_LANGUAGE,
      setStatus: (status) => set({ status }),
      setCurrentFile: (currentFile) => set({ currentFile }),
      setFiles: (files) => set({ files }),
      updateCurrentFile: (updates) =>
        set((state) => ({
          currentFile: state.currentFile
            ? { ...state.currentFile, ...updates }
            : updates,
        })),
      updateCurrentLanguage: (language) => set({ currentLanguage: language }),
    }),
    { name: "File Store" }
  )
);

// 创建注册状态store
export const useRegisterStore = create<RegisterState>()(
  devtools(
    (set) => ({
      currentLanguage: DEFAULT_LANGUAGE,
      updateCurrentLanguage: (language) => set({ currentLanguage: language }),
    }),
    { name: "Register Store" }
  )
);
